import amazonProducts from '../data/amazon-products.json';

// API URL constants 
/* const API_URL = 'http://0.0.0.0:5001'; */
const API_URL = 'https://comparit-backend.onrender.com';

/**
 * Calculate Levenshtein distance between two strings
 * @param {string} a - First string
 * @param {string} b - Second string
 * @returns {number} - Edit distance between strings
 */
export const levenshteinDistance = (a, b) => {
  const matrix = [];

  // Initialize matrix
  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  // Fill matrix
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      const cost = a[j - 1] === b[i - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1, // deletion
        matrix[i][j - 1] + 1, // insertion
        matrix[i - 1][j - 1] + cost // substitution
      );
    }
  }

  return matrix[b.length][a.length];
};

/**
 * Get Amazon products from localStorage or fallback to JSON file
 * @returns {Array} Array of Amazon products
 */
export const getAmazonProducts = () => {
  try {
    const savedProducts = localStorage.getItem('amazonProducts');
    if (savedProducts) {
      return JSON.parse(savedProducts);
    }
  } catch (e) {
    console.error('Error loading Amazon products from localStorage', e);
  }
  return amazonProducts;
};

/**
 * Find products in the Amazon database that match or are similar to the given product name
 * @param {string} productName - The product name to match
 * @returns {Object|null} - The matching product from Amazon database or null if not found
 */
export const findMatchingAmazonProduct = async (productName) => {
  if (!productName) return null;
  
  const products = getAmazonProducts();
  const normalizedInput = productName.toLowerCase().trim();
  
  // First try exact match
  const exactMatch = products.find(
    product => product.productName.toLowerCase() === normalizedInput
  );
  
  if (exactMatch) return exactMatch;
  
  // Then try partial match (if product name contains the database entry or vice versa)
  const partialMatch = products.find(
    product => 
      normalizedInput.includes(product.productName.toLowerCase()) || 
      product.productName.toLowerCase().includes(normalizedInput)
  );
  
  if (partialMatch) return partialMatch;
  
  // Finally, try fuzzy matching with Levenshtein distance
  const MAX_DISTANCE = 3; // Maximum allowed edit distance (adjustable)
  const DISTANCE_RATIO_THRESHOLD = 0.25; // Maximum ratio of distance to word length (adjustable)
  
  // Find the product with the smallest edit distance
  let bestMatch = null;
  let smallestDistance = Infinity;
  let bestDistanceRatio = Infinity;
  
  for (const product of products) {
    const productNameLower = product.productName.toLowerCase();
    const distance = levenshteinDistance(normalizedInput, productNameLower);
    const distanceRatio = distance / Math.max(normalizedInput.length, productNameLower.length);
    
    // Check if this product has a smaller distance and is within our thresholds
    if (
      distance <= MAX_DISTANCE && 
      distanceRatio <= DISTANCE_RATIO_THRESHOLD &&
      (distance < smallestDistance || (distance === smallestDistance && distanceRatio < bestDistanceRatio))
    ) {
      smallestDistance = distance;
      bestDistanceRatio = distanceRatio;
      bestMatch = product;
    }
  }
  
  if (bestMatch) return bestMatch;
  
  // If no match found locally, search online
  try {
    const response = await fetch(`${API_URL}/api/amazon-search`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ product: productName }),
    });
    
    if (!response.ok) {
      console.error("Amazon search API error:", response.statusText);
      return null;
    }
    
    const result = await response.json();
    
    if (result.found) {
      // Cache the result in localStorage for future use
      const newProducts = [...products, {
        productName: result.productName,
        amazonLink: result.amazonLink,
        price: result.price
      }];
      localStorage.setItem('amazonProducts', JSON.stringify(newProducts));
      
      return {
        productName: result.productName,
        amazonLink: result.amazonLink,
        price: result.price
      };
    }
  } catch (error) {
    console.error("Error searching for Amazon product:", error);
  }
  
  return null;
};

/**
 * Check if any product in the list has a matching Amazon product
 * @param {Array} products - List of product names to check
 * @returns {boolean} - True if at least one product has an Amazon match
 */
export const hasAmazonProducts = async (products) => {
  if (!products || !products.length) return false;
  
  // Check each product sequentially
  for (const product of products) {
    const match = await findMatchingAmazonProduct(product);
    if (match !== null) return true;
  }
  
  return false;
}; 